import {createContext,useContext,useId,useLayoutEffect,useRef,useState,type PointerEvent,type ReactNode} from 'react';
import {useCopy} from '@platform/ui/config';
import './BottomCta.css';

// App supplies only screen/navigation boundaries, never a streaming revision.
// A retained footer must reopen when returning from another action surface.
export const BottomCtaScreen=createContext('');

// All room/purchase footers share the surface gesture. Interactive controls and
// scrollable content retain their own actions; the handle supplies keyboard access.
function isSurfaceTarget(target:EventTarget|null,frame:HTMLElement){
 if(!(target instanceof Element))return false;
 if(target.closest('.bottom-cta-handle'))return true;
 if(target.closest('button,a,input,select,textarea,label,summary,[role="button"],[role="slider"],[contenteditable="true"]'))return false;
 for(let element:Element|null=target;element&&element!==frame;element=element.parentElement){
  const style=getComputedStyle(element);
  if((/auto|scroll/.test(style.overflowY)&&element.scrollHeight>element.clientHeight+1)||(/auto|scroll/.test(style.overflowX)&&element.scrollWidth>element.clientWidth+1))return false;
 }
 return true;
}
export function BottomCta({children,expansion,className='',revealKey,handleLabel,handleDetail,handleBeat,handleMeta,'data-ui':dataUi}:{children:ReactNode;expansion?:ReactNode;className?:string;revealKey?:string;handleLabel?:string;handleDetail?:ReactNode;handleBeat?:number;handleMeta?:{text:string;icon:ReactNode;dataUi?:string};'data-ui'?:string}){
 const t=useCopy(),id=useId(),content=useRef<HTMLDivElement>(null),frame=useRef<HTMLDivElement>(null);
 const screen=useContext(BottomCtaScreen);
 const [expanded,setExpanded]=useState(true),[height,setHeight]=useState(0),[dragHeight,setDragHeight]=useState<number|null>(null);
 const drag=useRef<{id:number;x:number;y:number;height:number;full:number;expanded:boolean;moved:boolean}|null>(null);
 const suppressClick=useRef(false);
 useLayoutEffect(()=>{
  const element=frame.current;if(!element)return;
  // Cancel native scrolling only for a gesture that starts on the surface.
  // An ancestor touch-action:none would also disable basket scrolling/sliders.
  const touchStart=(event:TouchEvent)=>{if(event.touches.length===1&&drag.current)event.preventDefault();};
  element.addEventListener('touchstart',touchStart,{passive:false});
  return()=>element.removeEventListener('touchstart',touchStart);
 },[]);
 useLayoutEffect(()=>{
  const element=content.current;if(!element)return;
  const measure=()=>setHeight(element.getBoundingClientRect().height);
  measure();const observer=new ResizeObserver(measure);observer.observe(element);
  return()=>observer.disconnect();
 },[]);
 useLayoutEffect(()=>{
  const element=frame.current,shell=element?.closest<HTMLElement>('.tombala-room');
  // The continuous room dock measures its own footer. Standalone previews keep
  // this fallback so announcements still clear their expanded purchase forms.
  if(!element||!shell||element.closest('dialog,.room-footer-dock'))return;
  const measure=()=>shell.style.setProperty('--room-bottom-cta-height',`${element.getBoundingClientRect().height}px`);
  measure();const observer=new ResizeObserver(measure);observer.observe(element);
  return()=>{observer.disconnect();shell.style.removeProperty('--room-bottom-cta-height');};
 },[]);
 // Each new screen/draw starts open, as does an explicit new card selection.
 // This preference is local to the mounted footer, never saved. Stream updates
 // and amount changes within that screen do not reset the chosen position.
 useLayoutEffect(()=>{setExpanded(true);setDragHeight(null);drag.current=null;},[revealKey,screen]);
 const start=(event:PointerEvent<HTMLDivElement>)=>{
  if(!event.isPrimary||event.button!==0||height===0||!isSurfaceTarget(event.target,event.currentTarget))return;
  suppressClick.current=false;
  const handle=event.currentTarget.querySelector<HTMLButtonElement>('.bottom-cta-handle');
  if(content.current?.contains(document.activeElement))handle?.focus({preventScroll:true});
  const visible=expanded?height:0;
  drag.current={id:event.pointerId,x:event.clientX,y:event.clientY,height:visible,full:height,expanded,moved:false};
  const capture=(event.target as Element).closest('.bottom-cta-handle')??event.currentTarget;
  capture.setPointerCapture(event.pointerId);
 };
 const move=(event:PointerEvent<HTMLDivElement>)=>{
  const current=drag.current;if(!current||current.id!==event.pointerId)return;
  const delta=current.y-event.clientY;if(Math.hypot(delta,event.clientX-current.x)>8)current.moved=true;
  if(current.moved)setDragHeight(Math.max(0,Math.min(current.full,current.height+delta)));
 };
 const finish=(event:PointerEvent<HTMLDivElement>,cancel=false)=>{
  const current=drag.current;if(!current||current.id!==event.pointerId)return;
  const delta=current.y-event.clientY;
  // Touch browsers may omit click after slight finger movement. Touch commits
  // here; mouse/keyboard keep the native click route.
  if(current.moved){
   suppressClick.current=true;
   const threshold=Math.min(48,current.full*.35);
   const vertical=Math.abs(delta)>Math.abs(event.clientX-current.x);
   setExpanded(cancel?current.expanded:vertical&&Math.abs(delta)>=threshold?delta>0:current.expanded);
  }else if(!cancel&&event.pointerType==='touch'){suppressClick.current=true;setExpanded(!current.expanded);}
  drag.current=null;setDragHeight(null);
  // Native pointer-up/cancel releases capture after dispatch. Releasing it here
  // can retarget the compatibility click to a CTA exposed by the panel resize.
 };
 const closed=!expanded&&dragHeight===null;
 return <div ref={frame} className={'bottom-cta '+className} data-ui={dataUi} data-state={expanded?'expanded':'collapsed'} data-dragging={dragHeight!==null}
  onPointerDown={start} onPointerMove={move} onPointerUp={event=>finish(event)}
  onPointerCancel={event=>finish(event,true)} onLostPointerCapture={event=>finish(event,true)}
  onClick={event=>{if(!isSurfaceTarget(event.target,event.currentTarget))return;const suppress=suppressClick.current&&event.detail!==0;suppressClick.current=false;if(!suppress)setExpanded(value=>!value);}}>
  <button type="button" className="bottom-cta-handle" data-labelled={!!handleLabel||!!handleMeta} aria-controls={id} aria-expanded={expanded}
   aria-label={t(expanded?'cta.collapse':'cta.expand')+(expanded&&handleMeta?' · '+handleMeta.text:'')}
   onKeyDown={event=>{if(event.key==='ArrowUp'||event.key==='ArrowDown'){event.preventDefault();setExpanded(event.key==='ArrowUp');}}}>
   <span className="bottom-cta-grip" aria-hidden="true"/>
   {handleLabel&&<span className="bottom-cta-handle-label"><span className="bottom-cta-label-text bottom-cta-blink" key={handleBeat} data-ticking={handleBeat!==undefined&&handleBeat>0}>{handleLabel}</span>{handleDetail&&<small className="bottom-cta-label-detail">{handleDetail}</small>}</span>}
   {handleMeta&&<span className="bottom-cta-handle-meta" data-ui={handleMeta.dataUi}><span>{handleMeta.text}</span>{handleMeta.icon}</span>}
  </button>
  <div id={id} className="bottom-cta-viewport" style={{height:dragHeight??(expanded?height||'auto':0)}} inert={closed} aria-hidden={closed}>
   <div ref={content} className="bottom-cta-content">
    {expansion&&<div className="bottom-cta-expansion">{expansion}</div>}
    <div className="bottom-cta-actions">{children}</div>
   </div>
  </div>
 </div>;
}
